import { Component, inject, OnInit, OnDestroy, Input, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardApiService } from '@mavles/dashboard';
import { IAccountAudit, IAccountAuditParams } from '@mavles/accounts';
import { ContactApiService, IClosureRequest, IContactClosureHistoryItem } from '@mavles/contacts';
import { AlertService } from '@mavles/shared';
import { AppService, AccountApiService } from '@mavles/user';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-accounts-audit',
  templateUrl: './accounts-audit.component.html',
  styleUrl: './accounts-audit.component.scss',
  standalone: false
})
export class AccountsAuditComponent implements OnInit, OnDestroy {
  private dashboardService = inject(DashboardApiService);
  private contactService = inject(ContactApiService);
  private alertService = inject(AlertService);
  private modalService = inject(NgbModal);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private appService = inject(AppService);
  private accountService = inject(AccountApiService);

  isAdmin = false;

  customerAccounts: IAccountAudit[] = [];
  displayedAccounts: IAccountAudit[] = [];
  loading = true;

  // Page Data / Inputs
  @Input() contactType = 'customer';
  @Input() showToolbar = true;
  pageTitle = 'Customer Accounts';

  // Filters
  contactStatus = ''; // '', '0', '1', 'pending'
  balanceFilter = 'all'; // 'all', 'due', 'zero', 'advance'
  startDate = '';
  endDate = '';
  contactName = '';
  datePreset = 'this_year';

  // KPI Metrics
  totalInvoiced = 0;
  totalPaid = 0;
  totalBalance = 0;
  openCount = 0;
  closedCount = 0;
  pendingCount = 0;
  dueCount = 0;

  // Closure Request Tracking
  pendingRequestsMap = new Map<number, IClosureRequest>();
  activeContact: IAccountAudit | null = null;
  closureActionComments = '';
  closureActionSubmitting = false;

  // Closure History Modal State
  historyLoading = false;
  closureHistory: IContactClosureHistoryItem[] = [];
  historyContactName = '';

  // Search Debounce
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription | undefined;

  // Pagination
  currentPage = 1;
  perPage = 15;
  totalRecords = 0;
  totalPages = 0;

  // Sorting
  sortColumn = 'created_at';
  sortDirection: 'asc' | 'desc' = 'desc';

  ngOnInit(): void {
    this.pageTitle = this.contactType === 'vendor' ? 'Vendor Accounts' : 'Customer Accounts';
    this.checkUserRole();

    // 1. Subscribe to Route Data (Type) if routed
    this.route.data.subscribe(data => {
      if (data && data['contactType']) {
        this.contactType = data['contactType'];
      }
      this.pageTitle = this.contactType === 'vendor' ? 'Vendor Accounts' : 'Customer Accounts';
    });

    // 2. Setup Search Debounce
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.updateQueryParams();
    });

    // 3. Subscribe to Query Params (Filters/State)
    this.route.queryParams.subscribe(params => {
      const today = new Date();
      const defaultStart = `${today.getFullYear()}-01-01`;
      const defaultEnd = `${today.getFullYear()}-12-31`;

      this.startDate = params['start_date'] || defaultStart;
      this.endDate = params['end_date'] || defaultEnd;
      this.contactStatus = params['status'] || '';
      this.balanceFilter = params['balance_filter'] || 'all';
      this.contactName = params['contact_name'] || '';

      this.currentPage = params['page'] ? Number(params['page']) : 1;
      this.perPage = params['per_page'] ? Number(params['per_page']) : 15;
      this.sortColumn = params['sort_by'] || 'created_at';
      this.sortDirection = params['sort_order'] || 'desc';

      this.fetchCustomerAccounts();
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onSearch(term: string): void {
    this.contactName = term;
    this.currentPage = 1;
    this.searchSubject.next(term);
  }

  clearSearch(): void {
    this.contactName = '';
    this.currentPage = 1;
    this.updateQueryParams();
  }

  updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        start_date: this.startDate || null,
        end_date: this.endDate || null,
        status: this.contactStatus === 'pending' ? null : (this.contactStatus || null),
        balance_filter: this.balanceFilter === 'all' ? null : this.balanceFilter,
        contact_name: this.contactName || null,
        page: this.currentPage === 1 ? null : this.currentPage,
        per_page: this.perPage === 15 ? null : this.perPage,
        sort_by: this.sortColumn === 'created_at' ? null : this.sortColumn,
        sort_order: this.sortDirection === 'desc' ? null : this.sortDirection
      },
      queryParamsHandling: 'merge'
    });
  }

  setDatePreset(preset: 'this_year' | 'this_month' | 'last_30' | 'all_time'): void {
    this.datePreset = preset;
    const now = new Date();
    if (preset === 'this_year') {
      this.startDate = `${now.getFullYear()}-01-01`;
      this.endDate = `${now.getFullYear()}-12-31`;
    } else if (preset === 'this_month') {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      this.startDate = `${year}-${month}-01`;
      this.endDate = `${year}-${month}-${lastDay}`;
    } else if (preset === 'last_30') {
      const past = new Date();
      past.setDate(now.getDate() - 30);
      this.startDate = past.toISOString().slice(0, 10);
      this.endDate = now.toISOString().slice(0, 10);
    } else {
      this.startDate = '';
      this.endDate = '';
    }
    this.currentPage = 1;
    this.updateQueryParams();
  }

  clearFilters(): void {
    const today = new Date();
    this.startDate = `${today.getFullYear()}-01-01`;
    this.endDate = `${today.getFullYear()}-12-31`;
    this.contactStatus = '';
    this.balanceFilter = 'all';
    this.contactName = '';
    this.datePreset = 'this_year';
    this.currentPage = 1;
    this.updateQueryParams();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.updateQueryParams();
  }

  setQuickStatus(status: '' | '0' | '1' | 'pending'): void {
    this.contactStatus = status;
    if (status === 'pending') {
      this.applyLocalFilters();
    } else {
      this.onFilterChange();
    }
  }

  setBalanceFilter(filter: string): void {
    this.balanceFilter = filter;
    this.applyLocalFilters();
  }

  onPageChange(page: number): void {
    if (this.currentPage === page) return;
    this.currentPage = page;
    this.updateQueryParams();
  }

  onPerPageChange(): void {
    this.currentPage = 1;
    this.updateQueryParams();
  }

  sort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.updateQueryParams();
  }

  fetchCustomerAccounts(): void {
    this.loading = true;
    const params: IAccountAuditParams = {
      page: this.currentPage,
      per_page: this.perPage,
      contact_type: this.contactType,
      sort_by: this.sortColumn,
      sort_order: this.sortDirection
    };

    if (this.contactStatus !== '' && this.contactStatus !== 'pending') {
      params.account_status = Number(this.contactStatus);
    }
    if (this.startDate) {
      params.start_date = this.startDate;
    }
    if (this.endDate) {
      params.end_date = this.endDate;
    }
    if (this.contactName) {
      params.contact_name = this.contactName;
    }

    this.dashboardService.getAccountsAudit(params).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.customerAccounts = response.data.data || [];
          this.totalRecords = response.data.total;
          this.totalPages = response.data.last_page;

          // Build pending requests map directly from latest_closure_log embedded in the response
          const map = new Map<number, IClosureRequest>();
          this.customerAccounts.forEach(c => {
            if (c.latest_closure_log && c.latest_closure_log.status === 'waiting_for_approval') {
              map.set(Number(c.contact_id), c.latest_closure_log as any);
            }
          });
          this.pendingRequestsMap = map;
        } else {
          this.customerAccounts = [];
          this.totalRecords = 0;
          this.pendingRequestsMap = new Map();
        }
        this.loading = false;
        this.applyLocalFilters();
      },
      error: (error) => {
        console.error('Accounts audit load error:', error);
        this.customerAccounts = [];
        this.loading = false;
        this.totalRecords = 0;
        this.pendingRequestsMap = new Map();
        this.applyLocalFilters();
      }
    });
  }

  applyLocalFilters(): void {
    let result = [...this.customerAccounts];

    // Status filter for pending requests
    if (this.contactStatus === 'pending') {
      result = result.filter(c => this.pendingRequestsMap.has(Number(c.contact_id)));
    }

    // Balance filter
    if (this.balanceFilter === 'due') {
      result = result.filter(c => Number(c.balance) > 0);
    } else if (this.balanceFilter === 'zero') {
      result = result.filter(c => Number(c.balance) === 0);
    } else if (this.balanceFilter === 'advance') {
      result = result.filter(c => Number(c.balance) < 0);
    }

    this.displayedAccounts = result;
    this.calculateMetrics();
  }

  calculateMetrics(): void {
    this.totalInvoiced = this.customerAccounts.reduce((acc, c) => acc + (Number(c.total_amount) || 0), 0);
    this.totalPaid = this.customerAccounts.reduce((acc, c) => acc + (Number(c.total_paid) || 0), 0);
    this.totalBalance = this.customerAccounts.reduce((acc, c) => acc + (Number(c.balance) || 0), 0);
    this.openCount = this.customerAccounts.filter(c => c.account_status === 0).length;
    this.closedCount = this.customerAccounts.filter(c => c.account_status === 1).length;
    this.pendingCount = this.customerAccounts.filter(c => this.pendingRequestsMap.has(Number(c.contact_id))).length;
    this.dueCount = this.customerAccounts.filter(c => Number(c.balance) > 0).length;
  }

  isPendingClosure(customer: IAccountAudit | number | null | undefined): boolean {
    if (!customer) return false;
    if (typeof customer === 'number') {
      const c = this.customerAccounts.find(x => x.contact_id === customer);
      return c?.latest_closure_log?.status === 'waiting_for_approval';
    }
    return customer.latest_closure_log?.status === 'waiting_for_approval';
  }

  isAccountClosed(customer: IAccountAudit | null | undefined): boolean {
    if (!customer) return false;
    return customer.account_status === 1 || customer.latest_closure_log?.status === 'closed';
  }

  canRequestClosure(customer: IAccountAudit | null | undefined): boolean {
    if (!customer) return false;
    return !this.isAccountClosed(customer) && !this.isPendingClosure(customer);
  }

  private checkUserRole(): void {
    this.isAdmin = this.appService.isAdminLoggedIn();
    this.accountService.getUser().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const u = res.data as any;
          this.isAdmin = u.is_admin === true ||
            (Array.isArray(u.roles) && u.roles.some((r: any) => (typeof r === 'string' ? r : r?.name)?.toUpperCase() === 'ADMIN'));
        }
      },
      error: () => {
        this.isAdmin = this.appService.isAdminLoggedIn();
      }
    });
  }

  canCancelClosure(customer: IAccountAudit | null | undefined): boolean {
    if (!this.isPendingClosure(customer)) return false;
    if (this.isAdmin) return true;
    const currentUserId = (this.appService.currentUser?.id || this.appService.getActiveUser()?.id);
    const req = this.getPendingRequest(customer);
    if (!req || !currentUserId) return false;
    return String(req.created_by) === String(currentUserId);
  }

  getPendingRequest(customer: IAccountAudit | null | undefined): any {
    if (!customer) return undefined;
    return customer.latest_closure_log?.status === 'waiting_for_approval'
      ? customer.latest_closure_log
      : this.pendingRequestsMap.get(Number(customer.contact_id));
  }

  // =========================================================================
  // Closure Workflow Modal Actions
  // =========================================================================

  openRequestModal(content: TemplateRef<any>, customer: IAccountAudit): void {
    this.activeContact = customer;
    this.closureActionComments = '';
    this.modalService.open(content, { centered: true });
  }

  submitRequestModal(modal: any): void {
    if (!this.activeContact) return;
    this.closureActionSubmitting = true;
    this.contactService.submitClosureRequest(this.activeContact.contact_id, this.closureActionComments).subscribe({
      next: (res) => {
        this.closureActionSubmitting = false;
        modal.close();
        this.alertService.success(res?.message || 'Closure request submitted successfully.');
        this.fetchCustomerAccounts();
      },
      error: (err) => {
        this.closureActionSubmitting = false;
        this.alertService.error(err?.error?.message || err?.message || 'Failed to submit closure request.');
      }
    });
  }

  openApproveModal(content: TemplateRef<any>, customer: IAccountAudit): void {
    this.activeContact = customer;
    this.closureActionComments = '';
    this.modalService.open(content, { centered: true });
  }

  submitApproveModal(modal: any): void {
    if (!this.activeContact) return;
    const reqId = this.activeContact.latest_closure_log?.request_id || this.pendingRequestsMap.get(Number(this.activeContact.contact_id))?.request_id;
    if (!reqId) {
      this.alertService.error('Pending request record not found.');
      return;
    }
    this.closureActionSubmitting = true;
    this.contactService.approveClosureRequest(reqId, this.closureActionComments).subscribe({
      next: (res) => {
        this.closureActionSubmitting = false;
        modal.close();
        this.alertService.success(res?.message || 'Account closure approved.');
        this.fetchCustomerAccounts();
      },
      error: (err) => {
        this.closureActionSubmitting = false;
        this.alertService.error(err?.error?.message || err?.message || 'Failed to approve closure.');
      }
    });
  }

  openRejectModal(content: TemplateRef<any>, customer: IAccountAudit): void {
    this.activeContact = customer;
    this.closureActionComments = '';
    this.modalService.open(content, { centered: true });
  }

  submitRejectModal(modal: any): void {
    if (!this.activeContact) return;
    if (!this.closureActionComments?.trim()) {
      this.alertService.error('Rejection reason / comments are mandatory.');
      return;
    }
    const reqId = this.activeContact.latest_closure_log?.request_id || this.pendingRequestsMap.get(Number(this.activeContact.contact_id))?.request_id;
    if (!reqId) {
      this.alertService.error('Pending request record not found.');
      return;
    }
    this.closureActionSubmitting = true;
    this.contactService.rejectClosureRequest(reqId, this.closureActionComments).subscribe({
      next: (res) => {
        this.closureActionSubmitting = false;
        modal.close();
        this.alertService.success(res?.message || 'Closure request rejected.');
        this.fetchCustomerAccounts();
      },
      error: (err) => {
        this.closureActionSubmitting = false;
        this.alertService.error(err?.error?.message || err?.message || 'Failed to reject closure.');
      }
    });
  }

  openCancelModal(content: TemplateRef<any>, customer: IAccountAudit): void {
    this.activeContact = customer;
    this.closureActionComments = '';
    this.modalService.open(content, { centered: true });
  }

  submitCancelModal(modal: any): void {
    if (!this.activeContact) return;
    const reqId = this.activeContact.latest_closure_log?.request_id || this.pendingRequestsMap.get(Number(this.activeContact.contact_id))?.request_id;
    if (!reqId) {
      this.alertService.error('Pending request record not found.');
      return;
    }
    this.closureActionSubmitting = true;
    this.contactService.cancelClosureRequest(reqId, this.closureActionComments).subscribe({
      next: (res) => {
        this.closureActionSubmitting = false;
        modal.close();
        this.alertService.success(res?.message || 'Closure request cancelled.');
        this.fetchCustomerAccounts();
      },
      error: (err) => {
        this.closureActionSubmitting = false;
        this.alertService.error(err?.error?.message || err?.message || 'Failed to cancel request.');
      }
    });
  }

  openHistoryModal(content: TemplateRef<any>, customer: IAccountAudit): void {
    this.activeContact = customer;
    this.historyContactName = customer.name;
    this.modalService.open(content, { centered: true, size: 'lg' });

    if (customer.closure_logs && customer.closure_logs.length > 0) {
      this.closureHistory = customer.closure_logs as any;
      this.historyLoading = false;
    } else {
      this.historyLoading = true;
      this.closureHistory = [];
      this.contactService.getClosureHistory(customer.contact_id).subscribe({
        next: (res: any) => {
          this.historyLoading = false;
          if (res && res.success && res.data) {
            if (Array.isArray(res.data)) {
              this.closureHistory = res.data;
            } else if (res.data.history && Array.isArray(res.data.history)) {
              this.closureHistory = res.data.history;
            }
          }
        },
        error: (err) => {
          this.historyLoading = false;
          console.error('Error fetching closure history:', err);
        }
      });
    }
  }

  exportToCsv(): void {
    if (!this.displayedAccounts || this.displayedAccounts.length === 0) {
      this.alertService.warn('No records available to export.');
      return;
    }
    const headers = ['Contact ID', 'Name', 'Type', 'Total Invoiced', 'Total Paid', 'Balance', 'Status', 'Created At'];
    const rows = this.displayedAccounts.map(c => [
      c.contact_id,
      `"${(c.name || '').replace(/"/g, '""')}"`,
      c.contact_type || this.contactType,
      c.total_amount,
      c.total_paid,
      c.balance,
      this.isPendingClosure(c) ? 'Pending Approval' : (this.isAccountClosed(c) ? 'Closed' : 'Open'),
      c.created_at
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${this.contactType}-accounts-audit-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.alertService.success('CSV export generated successfully.');
  }

  getInitials(name?: string): string {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
}


